import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, MenuBook } from "@mui/icons-material";
import periodicTableImg from "core/assets/images/periodic-table.png";
import nuclideGraphImg from "core/assets/images/nuclide-graph.png";

const DefinitionDialog = () => {
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <>
      <Tooltip title="Isotope App Definitions">
        <IconButton
          color="inherit"
          size="small"
          onClick={() => {
            setOpen(true);
          }}
        >
          <MenuBook />
        </IconButton>
      </Tooltip>
      <Dialog
        onClose={handleClose}
        open={open}
        fullScreen
        sx={{ maxWidth: "1200px", margin: "0 auto" }}
      >
        <DialogTitle>
          <Stack spacing={1} direction="row" width="100%" alignItems="center">
            <MenuBook />
            <Typography width="100%" variant="h6" fontWeight="bold">
              Isotopes App Definitions
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography>
            <b>Element &mdash;</b> An Element is substance whose atoms all have
            the same number of protons (that is, they have the same Atomic
            Number). Elements cannot be changed or broken down into simpler
            substances by chemical means.
          </Typography>
          <br />
          <Typography>
            <b>Isotope &mdash;</b> An Isotope is a type of atom within a
            specific element that has the same number of protons but a varying
            number of neutrons within the nucleus. Since the number of protons
            remains the same, all isotopes of an element have the same chemical
            properties. Because the atom's mass is almost entirely defined by
            the mass of the nucleus (protons and neutrons), changes in the
            number of neutrons means that each isotope of an element has a
            different mass.
          </Typography>
          <br />
          <Typography>
            <b>Atomic Number &mdash;</b> Number of protons in the nucleus of an
            atom.
          </Typography>
          <br />
          <Typography>
            <b>Atomic Mass Number &mdash;</b> Number of protons plus the number
            of neutrons in the nucleus of an atom.
          </Typography>
          <br />
          <Typography>
            <b>Nuclides &mdash;</b> The set of isotopes for a particular
            chemical element
          </Typography>
          <br />
          <Typography>
            <b>Periodic Table of the Elements &mdash;</b> The Periodic Table of
            the Elements is a tabular organization of the chemical elements. The
            table is arranged according to the electron configurations of atoms.
            The first Periodic Table was developed in 1869 by the Russian
            chemist Dmitri Mendeleev.
          </Typography>
          <Box
            component="img"
            width="100%"
            src={periodicTableImg}
            alt="periodic table"
          />
          <br />
          <br />
          <Typography>
            <b>Nuclide Graph (Chart of the Nuclides) &mdash;</b> The Nuclide
            Graph is a graphical display of all of the isotopes of the chemical
            elements. The vertical axis of the graph represents the number of
            protons which corresponds to the elements, and the horizontal axis
            of the graph represents the number of neutrons which determines the
            isotope of the element. Isotopes with the proper ratio of protons
            and neutrons are stable. Isotopes with too many or too few neutrons
            are considered radioactive. The "Line of Stability" is shown on the
            graph as a dark line of isotopes down the middle. Radioactive
            isotopes emit radiation as they rearrange their nucleus to become
            more stable and move towards the Line of Stability. Typically, the
            farther away from the Line of Stability, the more radioactive an
            isotope is and the faster it will change (decay) to a new isotope.
            Isotopes above and to the left of the Line of Stability typically
            decay by emitting a Positron or an X-Ray from Electron Capture.
            Isotopes below and to the right of the Line of Stability typically
            decay by emitting a Beta Minus Particle. Isotopes above and to the
            right of the Line of Stability (beyond the end of the line)
            typically decay by emitting an Alpha Particle.
          </Typography>
          <Box
            component="img"
            width="100%"
            src={nuclideGraphImg}
            alt="nuclide graph"
          />
          <br /> <br />
          <Typography>
            <b>Stable Isotopes &mdash;</b> Isotopes with the proper balance
            between protons and neutrons. They do not change their structure,
            nor do they emit radiation. (colored black{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              bgcolor="#000000"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Unstable/Radioactive Isotopes &mdash;</b> Isotopes with an
            improper balance between protons and neutrons. They rearrange their
            structure and emit radiation in the form of particles and/or rays as
            they become more stable (called radioactive decay).
          </Typography>
          <br />
          <Typography>
            <b>Alpha Decay &mdash;</b> A form of radioactive decay in which a
            high energy Helium nucleus is emitted from the parent nucleus. An
            alpha particle consists of two protons and two neutrons with no
            electrons. It has a mass of 4 amu and a charge of +2 ecu. Helium
            found on Earth comes from the decay of terrestrial radioactive
            materials. (colored yellow{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              border="1px solid black"
              bgcolor="#ffff42"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Beta Minus Decay &mdash;</b> A form of radioactive decay in which
            a high energy negative electron is emitted from the parent nucleus.
            This occurs when a neutral neutron in the nucleus converts to a
            positive proton and ejects the negative beta particle. A beta minus
            particle has a very small mass and a charge of -1 ecu. (colored pink{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              border="1px solid black"
              bgcolor="#e78cc6"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Positron Emission &mdash;</b> A form of radioactive decay in
            which a high energy positive electron is emitted from the parent
            nucleus. This occurs when a positive proton converts to a neutral
            neutron in the nucleus and ejects the positive beta particle. A
            positron has a very small mass and a charge of +1 ecu. It is
            considered “antimatter” and will annihilate when it comes in contact
            with a negative electron. (colored blue{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              border="1px solid black"
              bgcolor="#63c6de"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Electron Capture &mdash;</b> A form of radioactive decay in which
            an electron passes close to the nucleus and is captured by the
            nucleus. The captured electron combines with a positive proton to
            create a neutral neutron in the nucleus. As electrons move within
            the electron cloud to fill in the missing electron x-rays are
            emitted from the atom's electron cloud. (colored blue{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              border="1px solid black"
              bgcolor="#63c6de"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Gamma Emission &mdash;</b> A high energy photon (beam of light)
            that is emitted from an unstable atom's nucleus as it becomes more
            stable. Usually accompanies an alpha or beta decay.
          </Typography>
          <br />
          <Typography>
            <b>X-Ray Emission &mdash;</b> A high energy photon (beam of light)
            that is emitted from an unstable atom's electron cloud as it becomes
            more stable. Usually accompanies an electron capture event.
          </Typography>
          <br />
          <Typography>
            <b>Spontaneous Fission &mdash;</b> The process in which an unstable
            atom splits into two parts in order to become more stable. Usually
            occurs in very heavy isotopes. (colored green{" "}
            <Box
              display="inline-block"
              width="12px"
              component="span"
              height="12px"
              border="1px solid black"
              bgcolor="#52b552"
            />{" "}
            in the nuclide graph).
          </Typography>
          <br />
          <Typography>
            <b>Radioactive Decay Chain &mdash;</b> A sequence of decay of an
            unstable atom leading to a more stable atom. Often times an unstable
            parent atom will decay into a more stable yet still radioactive
            daughter atom. That daughter atom will further decay. This sequence
            continues as a chain until a stable atom is formed.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DefinitionDialog;
